import type { Pillar, Slot } from '../types/content'

export const WINDOW_START = new Date('2026-08-08T00:00:00Z')
export const WINDOW_END = new Date('2026-09-30T00:00:00Z')
export const WINDOW_DAYS = Math.round((WINDOW_END.getTime() - WINDOW_START.getTime()) / 86400000) + 1

export function dayIndex(iso: string): number {
  return Math.round((new Date(`${iso}T00:00:00Z`).getTime() - WINDOW_START.getTime()) / 86400000)
}

/** Left / width percentages for a bar spanning two dates inside the plan window. */
export function span(startIso: string, endIso: string): { left: number; width: number } {
  const start = dayIndex(startIso)
  const end = dayIndex(endIso)
  return {
    left: (start / WINDOW_DAYS) * 100,
    width: ((end - start + 1) / WINDOW_DAYS) * 100,
  }
}

export const pillarStyles: Record<Pillar, { text: string; bg: string; border: string; dot: string; hex: string }> = {
  Evergreen: { text: 'text-evergreen', bg: 'bg-evergreen/10', border: 'border-evergreen/30', dot: 'bg-evergreen', hex: 'rgb(var(--evergreen))' },
  Product: { text: 'text-productc', bg: 'bg-productc/10', border: 'border-productc/30', dot: 'bg-productc', hex: 'rgb(var(--productc))' },
  Campaign: { text: 'text-campaign', bg: 'bg-campaign/10', border: 'border-campaign/30', dot: 'bg-campaign', hex: 'rgb(var(--campaign))' },
}

const NETWORKS: { key: string; label: string; match: (c: string) => boolean }[] = [
  { key: 'certs', label: 'Certificates.Dev', match: (c) => c.includes('Certs') },
  { key: 'unlearn', label: 'Unlearn', match: (c) => c.includes('Unlearn') },
  { key: 'bb', label: 'BitterBrains LI', match: (c) => c.includes('BB LI') },
  { key: 'vs', label: 'Vue School', match: (c) => c.startsWith('VS') || c.includes(' VS') },
  { key: 'vuecert', label: 'Vue Cert LI', match: (c) => c.includes('Vue Cert LI') },
  { key: 'mn', label: 'Mastering Nuxt', match: (c) => c.includes('MN ') },
  { key: 'fn', label: 'Frontend Nation', match: (c) => c.includes('FN ') },
  { key: 'community', label: 'Vue community (VJD/VJF/VJN)', match: (c) => /VJ[DFN]/.test(c) || c.includes('NN BS') },
]

export function channelLoad(slots: Slot[]): { label: string; count: number }[] {
  return NETWORKS.map((n) => ({ label: n.label, count: slots.filter((s) => n.match(s.channels)).length }))
    .filter((n) => n.count > 0)
    .sort((a, b) => b.count - a.count)
}

export function countByPillar(slots: Slot[]): Record<Pillar, number> {
  return slots.reduce(
    (acc, s) => {
      acc[s.pillar] += 1
      return acc
    },
    { Evergreen: 0, Product: 0, Campaign: 0 } as Record<Pillar, number>,
  )
}

export function groupByDay(slots: Slot[]): { day: string; date: string; slots: Slot[] }[] {
  const map = new Map<string, { day: string; date: string; slots: Slot[] }>()
  slots.forEach((s) => {
    const entry = map.get(s.date) ?? { day: s.day, date: s.date, slots: [] }
    entry.slots.push(s)
    map.set(s.date, entry)
  })
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date))
}

export function blockedSlots(slots: Slot[]): Slot[] {
  return slots.filter((s) => s.blocked)
}
